import { CartItem, CatalogueItem } from '@vfs-digital-channels/ns-utils';
import {
  EVENT_NAME,
  FORM_NAME,
  JOURNEY_NAME,
  JOURNEY_STEP_NAME,
  Link,
  LINK_NAME,
  LINK_PREFIX,
  PAGE_CHANNEL,
  PAGE_ERROR_CODE,
  PAGE_ERROR_NAME,
  PAGE_ERROR_TYPE,
  PAGE_NAME,
  PAGE_NAME_PREFIX,
  PAGE_SECTION,
  PAGE_SUB_DOMAIN,
  PAGE_TYPE,
  StaticVariables,
  View,
  VISITOR_CUSTOMER_TYPE,
} from 'src/features/common/types/tealium-service';
import objTealium from 'src/utils/Tealium/Tealium';
import { getProductListItems } from '../order/order-tealium-service';
import { getProductListItemsFromCart } from 'src/features/onboarding/services/onboarding-tealium-services';

const attempt = (func: () => void) => {
  try {
    func();
  } catch (err) {
    // @ts-ignore
    console.error(err);
    // intentional swallowing of non functional error
  }
};

const sendViewToTealium = (obj: View) => {
  const viewObj = {
    ...obj,
    event_name: obj.event_name.join(', '),
  };
  attempt(() => {
    objTealium.view(viewObj);
  });
};

const sendLinkToTealium = (obj: Link) => {
  const linkObj = {
    ...obj,
    event_name: obj.event_name.join(', '),
  };
  attempt(() => {
    // @ts-ignore
    objTealium.link(linkObj);
  });
};

const staticDeviceActivationVariables: StaticVariables = {
  page_url: window.location.href,
  page_section: PAGE_SECTION.BUY_POS,
  page_type: PAGE_TYPE.WEBSITE,
  page_subdomain: PAGE_SUB_DOMAIN.BUY_POS,
  page_channel: PAGE_CHANNEL.BUY_POS,
  visitor_customer_type: VISITOR_CUSTOMER_TYPE.CONSUMER,
};

export interface IDeviceActivationTealiumService {
  onPersonalDetailsStart(): void;
  onPersonalDetailsIdle(incompleteFields: string, formCompletionRate: string): void;
  onPersonalDetailsShowAddress(): void;
  onPersonalDetailsEnd(): void;
  onWebCamCheckStart(transactionId: string | undefined): void;
  onWebCamAction(
    action: "i've enabled it" | "i'll be back",
    transactionId: string | undefined,
  ): void;
  onEnterBarcodeStart(transactionId: string | undefined): void;
  onEnterBarcodeEnd(transactionId: string | undefined): void;
  onEnterBarcodeError(transactionId: string | undefined): void;
  onAssignNameStart(transactionId: string | undefined): void;
  onAddNewDevice(transactionId: string | undefined): void;
  onDeleteDevice(transactionId: string | undefined): void;
  onAssignNameEnd(transactionId: string | undefined): void;
  onConfirmationModalStart(transactionId: string | undefined): void;
  onConfirmationModalEnd(
    action: 'cancel' | 'delete card machine',
    transactionId: string | undefined,
  ): void;
  onDeviceActivationJourneyCompleted(
    transactionId: string | undefined,
    productList: CatalogueItem[],
    cart?: CartItem[],
  ): void;
}

export class DeviceActivationTealiumService implements IDeviceActivationTealiumService {
  private static staticDeviceActivationVariables = staticDeviceActivationVariables;

  constructor(private journeyName: string = JOURNEY_NAME.DEVICE_ACTIVATION) {}

  /** PERSONAL DETAILS */
  onPersonalDetailsStart() {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW, EVENT_NAME.FORM_START, EVENT_NAME.JOURNEY_START],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.PERSONAL_DETAILS}`,
      form_name: FORM_NAME.PERSONAL_DETAILS,
      journey_name: JOURNEY_NAME.DEVICE_ACTIVATION,
      ...DeviceActivationTealiumService.staticDeviceActivationVariables,
    });
  }

  onPersonalDetailsIdle(incompleteFields: string, formCompletionRate: string) {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.PERSONAL_DETAILS}`,
      form_name: FORM_NAME.PERSONAL_DETAILS,
      form_completion_rate: `${formCompletionRate}%`,
      form_field_incomplete: incompleteFields,
      journey_name: JOURNEY_NAME.DEVICE_ACTIVATION,
      ...DeviceActivationTealiumService.staticDeviceActivationVariables,
    });
  }

  onPersonalDetailsShowAddress() {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      link_page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.PERSONAL_DETAILS}`,
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.PERSONAL_DETAILS}`,
      link_id: `${LINK_PREFIX.BUY_POS}${LINK_NAME.SHOW_BUSINESS_ADDRESS}`,
      form_name: FORM_NAME.PERSONAL_DETAILS,
      journey_name: JOURNEY_NAME.DEVICE_ACTIVATION,
      ...DeviceActivationTealiumService.staticDeviceActivationVariables,
    });
  }

  onPersonalDetailsEnd() {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION, EVENT_NAME.FORM_END],
      link_page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.PERSONAL_DETAILS}`,
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.PERSONAL_DETAILS}`,
      link_id: `${LINK_PREFIX.BUY_POS}${LINK_NAME.CHECKOUT}`,
      form_name: FORM_NAME.PERSONAL_DETAILS,
      journey_name: JOURNEY_NAME.DEVICE_ACTIVATION,
      ...DeviceActivationTealiumService.staticDeviceActivationVariables,
    });
  }

  /** WEBCAM CHECKER */
  onWebCamCheckStart(transactionId: string | undefined) {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW, EVENT_NAME.JOURNEY_STEP],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.WEBCAM}`,
      journey_name: JOURNEY_NAME.DEVICE_ACTIVATION,
      journey_step_name: JOURNEY_STEP_NAME.WEBCAM_ENABLE,
      transaction_id: transactionId,
      ...DeviceActivationTealiumService.staticDeviceActivationVariables,
    });
  }

  onWebCamAction(action: "i've enabled it" | "i'll be back", transactionId: string | undefined) {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      link_page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.WEBCAM}`,
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.WEBCAM}`,
      link_name: `${LINK_PREFIX.BUY_POS}${LINK_NAME.WEBCAM}: ${action}`,
      journey_name: JOURNEY_NAME.DEVICE_ACTIVATION,
      transaction_id: transactionId,
      ...DeviceActivationTealiumService.staticDeviceActivationVariables,
    });
  }

  /** ENTER BARCODE */
  onEnterBarcodeStart(transactionId: string | undefined) {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW, EVENT_NAME.JOURNEY_STEP],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.ENTER_BARCODE}`,
      journey_name: JOURNEY_NAME.DEVICE_ACTIVATION,
      journey_step_name: JOURNEY_STEP_NAME.ENTER_BARCODE,
      transaction_id: transactionId,
      ...DeviceActivationTealiumService.staticDeviceActivationVariables,
    });
  }

  onEnterBarcodeEnd(transactionId: string | undefined) {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      link_page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.ENTER_BARCODE}`,
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.ENTER_BARCODE}`,
      link_name: `${LINK_PREFIX.BUY_POS}${LINK_NAME.ENTER_BARCODE}`,
      journey_name: JOURNEY_NAME.DEVICE_ACTIVATION,
      transaction_id: transactionId,
      ...DeviceActivationTealiumService.staticDeviceActivationVariables,
    });
  }

  onEnterBarcodeError(transactionId: string | undefined) {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_ERROR, EVENT_NAME.PAGE_VIEW],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.ENTER_BARCODE}`,
      journey_name: JOURNEY_NAME.DEVICE_ACTIVATION,
      transaction_id: transactionId,
      page_error_name: PAGE_ERROR_NAME.INCORRECT_BARCODE,
      page_error_type: PAGE_ERROR_TYPE.USER_ERROR,
      page_error_code: PAGE_ERROR_CODE.NA,
      ...DeviceActivationTealiumService.staticDeviceActivationVariables,
    });
  }

  /** ASSIGN NAME */
  onAssignNameStart(transactionId: string | undefined) {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW, EVENT_NAME.JOURNEY_STEP],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.ASSIGN_NAME}`,
      journey_name: JOURNEY_NAME.DEVICE_ACTIVATION,
      journey_step_name: JOURNEY_STEP_NAME.ASSIGN_NAME,
      transaction_id: transactionId,
      ...DeviceActivationTealiumService.staticDeviceActivationVariables,
    });
  }

  onAddNewDevice(transactionId: string | undefined) {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      link_page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.ASSIGN_NAME}`,
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.ASSIGN_NAME}`,
      link_name: `${LINK_PREFIX.BUY_POS}${LINK_NAME.ADD_NEW_DEVICE}`,
      journey_name: JOURNEY_NAME.DEVICE_ACTIVATION,
      transaction_id: transactionId,
      ...DeviceActivationTealiumService.staticDeviceActivationVariables,
    });
  }

  onDeleteDevice(transactionId: string | undefined) {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      link_page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.ASSIGN_NAME}`,
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.ASSIGN_NAME}`,
      link_name: `${LINK_PREFIX.BUY_POS}${LINK_NAME.DELETE_DEVICE}`,
      journey_name: JOURNEY_NAME.DEVICE_ACTIVATION,
      transaction_id: transactionId,
      ...DeviceActivationTealiumService.staticDeviceActivationVariables,
    });
  }

  onAssignNameEnd(transactionId: string | undefined) {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      link_page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.ASSIGN_NAME}`,
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.ASSIGN_NAME}`,
      link_name: `${LINK_PREFIX.BUY_POS}${LINK_NAME.ASSIGN_NAME_NEXT}`,
      journey_name: JOURNEY_NAME.DEVICE_ACTIVATION,
      transaction_id: transactionId,
      ...DeviceActivationTealiumService.staticDeviceActivationVariables,
    });
  }

  /** DELETE DEVICE MODAL */
  onConfirmationModalStart(transactionId: string | undefined) {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW, EVENT_NAME.JOURNEY_STEP],
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.ASSIGN_NAME_CONFIRMATION_MODAL}`,
      journey_name: JOURNEY_NAME.DEVICE_ACTIVATION,
      journey_step_name: JOURNEY_STEP_NAME.ASSIGN_NAME_CONFIRMATION_MODAL,
      transaction_id: transactionId,
      ...DeviceActivationTealiumService.staticDeviceActivationVariables,
    });
  }

  onConfirmationModalEnd(
    action: 'cancel' | 'delete card machine',
    transactionId: string | undefined,
  ) {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      link_page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.ASSIGN_NAME_CONFIRMATION_MODAL}`,
      page_name: `${PAGE_NAME_PREFIX.BUY_POS}${PAGE_NAME.ASSIGN_NAME_CONFIRMATION_MODAL}`,
      link_name: `${LINK_PREFIX.BUY_POS}${LINK_NAME.ASSIGN_NAME_CONFIRMATION_MODAL}: ${action}`,
      journey_name: JOURNEY_NAME.DEVICE_ACTIVATION,
      transaction_id: transactionId,
      ...DeviceActivationTealiumService.staticDeviceActivationVariables,
    });
  }

  /** JOURNEY COMPLETION */
  onDeviceActivationJourneyCompleted(
    transactionId: string | undefined,
    productList: CatalogueItem[],
    cart?: CartItem[],
  ) {
    sendViewToTealium({
      event_name: [
        EVENT_NAME.PAGE_VIEW,
        EVENT_NAME.JOURNEY_END,
        EVENT_NAME.MERCHANT_ONBOARDING_COMPLETE,
      ],
      page_name: `${PAGE_NAME_PREFIX.ONBOARDING}${PAGE_NAME.ONBOARDING_COMPLETE}`,
      journey_name: JOURNEY_NAME.DEVICE_ACTIVATION,
      transaction_id: transactionId,
      ...(cart ? getProductListItemsFromCart(cart) : getProductListItems(productList, true)),
      ...DeviceActivationTealiumService.staticDeviceActivationVariables,
    });
  }
}
